// import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PADSideNavbar from './components/PADSideNavbar';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, InformationCircleIcon, CheckCircleIcon, PlusCircleIcon, TrashIcon, PencilIcon } from '@heroicons/react/solid';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import { useAuth } from '../../../hooks/useAuth';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import {
  getPadStageOrder,
  getPadSidebarIndex,
  getPadSidebarSections,
  readIsCarryOutHealthChecksNow,
  readPatientTreatmentPreferences,
  buildPadSidebarUnlockFlags,
} from '../../../utils/padNavigationUtils';

// Constants for section titles and navigation links
const CURRENT_STAGE_TITLE = 'Post-Assessment Discussion';
const CURRENT_SECTION_NUMBER = 5;
const CURRENT_SECTION_TITLE = 'Agreed Medication Plan';

const PREV_SECTION_LINK = '/proforma/post-assessment-discussion/health-checks';
const PREV_SECTION_TITLE = "Health Checks";

export default function PADAMP() {
  const navigate = useNavigate();
  const { patientId } = useParams();

  const user = auth.currentUser;
  const clinicianId = user?.uid;

  const { claims } = useAuth();

  // Tracks whether the clinician has made any Firestore-persisted changes that
  // would require AI regeneration. Set to true in exactly three user-initiated
  // data change paths: addMedication (add/edit), remove medication inline, and
  // handleCheckboxChange (consent checkboxes). Dialog-local state changes like
  // titration edits do NOT set this because they only persist on "Save Medication
  // Plan" which routes through addMedication.
  const [hasChanges, setHasChanges] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Change state for managing medications (from array to single object)
  const [medication, setMedication] = useState(null);
  const [isAddMedicationDialogOpen, setIsAddMedicationDialogOpen] = useState(false);

  // New state for titration schedule
  const [titrationSchedule, setTitrationSchedule] = useState([
    { week: 1, instructions: "" },
    { week: 2, instructions: "" },
    { week: 3, instructions: "" },
    { week: 4, instructions: "" }
  ]);

  // New state for step-by-step dialog
  // Track the selected options at each step
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [showContraindications, setShowContraindications] = useState(false);
  const [selectedFormulation, setSelectedFormulation] = useState(null);
  const [selectedMedicineBrand, setSelectedMedicineBrand] = useState(null);
  const [selectedDosage, setSelectedDosage] = useState(null);
  const [otherMedicineBrandText, setOtherMedicineBrandText] = useState("");
  const [otherDosageText, setOtherDosageText] = useState("");  

   // Track which step we're on in the medication dialog
   const [dialogStep, setDialogStep] = useState(1);
  
  // Define medication options as a flat array for the list-based UI
  const medicationOptions = [
    "Methylphenidate Hydrochloride",
    "Lisdexamfetamine Mesilate",
    "Atomoxetine",
    "Dexamfetamine Sulfate",
    "Guanfacine",
    // "Other"
  ];
  
  // Define formulation options based on medication
  const getFormulationOptions = (medication) => {
    switch(medication) {
      case "Guanfacine":
      case "Lisdexamfetamine Mesilate":
        return ["N/A"];
      case "Dexamfetamine Sulfate":
        return ["Immediate-release (IR)", "Modified-release", "Liquid solution"];
      case "Atomoxetine":
        return ["N/A", "Liquid solution"];
      case "Methylphenidate Hydrochloride":
        return ["Modified-release", "Immediate-release (IR)", "Liquid solution"];
      default:
        return ["Extended-release (ER)", "Immediate-release (IR)", "N/A"];
    }
  };
  
  // Define medicine brand options based on medication and formulation
const getMedicineBrandOptions = (medication, formulation) => {
  switch(medication) {
    case "Guanfacine":
      return ["Intuniv", 
        // "Other"
      ];
    case "Lisdexamfetamine Mesilate":
      return ["Elvanse®", 
        "Elvanse Adult®", 
        // "Other"
      ];
    case "Dexamfetamine Sulfate":
      switch(formulation) {
        case "Immediate-release (IR)":
          return ["Dexamfetamine", "Amfexa", 
            // "Other"
          ];
        case "Modified-release":
          return ["Dexedrine", 
            // "Other"
          ];
        case "Liquid solution":
          return ["Dexamfetamine", 
            // "Other"
          ];
        default:
          return ["Other"];
      }
    case "Atomoxetine":
      switch(formulation) {
        case "N/A":
          return ["Atomoxetine", 
            // "Other"
          ];
        case "Liquid solution":
          return ["Strattera", 
            // "Other"
          ];
        default:
          return ["Other"];
      }
    case "Methylphenidate Hydrochloride":
      switch(formulation) {
        case "Modified-release":
          return [
            "Concerta XL (10 to 12 hour duration)",
            "Xaggitin XL (12 hour duration)",
            "Delmosart PR (12 hour duration)",
            "Medikinet XL (8 hour duration)",
            "Equasym XL (8 hour duration)",
            "Methylphenidate",
            "Xenidate XL",
            "Ritalin SR",
            "Affenid XL",
            "Matoride XL",
            "Meflynate XL",
            "Metyrol XL",
            "Ritalin XL",
            // "Other"
          ];
        case "Immediate-release (IR)":
          return [
            "Methylphenidate (4 hour duration)",
            "Ritalin (4 hour duration)",
            "Medikinet (<12 hour duration)",
            "Tranquilyn",
            // "Other"
          ];
        case "Liquid solution":
          return [
            "Methylphenidate", 
            // "Other"
          ];
        default:
          return ["Other"];
      }
    default:
      return ["Other"];
  }
};
  
  // Define dosage options based on medication, formulation, and brand
  const getDosageOptions = (medication, formulation, medicineBrand) => {
    // If the brand is "Other", we can't provide specific dosage options
    if (medicineBrand === "Other") {
      return ["Other"];
    }

    if (medication === "Guanfacine" && medicineBrand === "Intuniv") {
      return [
        "1 mg tablet",
        "2 mg tablet",
        "3 mg tablet",
        "4 mg tablet",
        // "Other"
      ];
    } else if (medication === "Lisdexamfetamine Mesilate") {
      // If the brand is "Other", we can't provide specific dosage options
      if (medicineBrand === "Other") {
        return ["Other"];
      }
      
      if (medicineBrand === "Elvanse®") {
        return [
          "20 mg capsule",
          "30 mg capsule",
          "40 mg capsule",
          "50 mg capsule",
          "60 mg capsule",
          "70 mg capsule",
          // "Other"
        ];
      } else if (medicineBrand === "Elvanse Adult®") {
        return [
          "30 mg capsule",
          "50 mg capsule",
          "70 mg capsule",
          // "Other"
        ];
      }    
    } else if (medication === "Dexamfetamine Sulfate") {
      // If the brand is "Other", we can't provide specific dosage options
      if (medicineBrand === "Other") {
        return ["Other"];
      }
      
      // Otherwise, provide dosage options based on formulation and brand
      switch(formulation) {
        case "Immediate-release (IR)":
          if (medicineBrand === "Dexamfetamine") {
            return [
              "5 mg tablet", 
              // "Other"
            ];
          } else if (medicineBrand === "Amfexa") {
            return [
              "5 mg tablet",
              "10 mg tablet",
              "20 mg tablet",
              // "Other"
            ];
          }
          break;
        case "Modified-release":
          if (medicineBrand === "Dexedrine") {
            return [
              "5 mg spansule",
              "10 mg spansule",
              "15 mg spansule",
              // "Other"
            ];
          }
          break;
        case "Liquid solution":
          if (medicineBrand === "Dexamfetamine") {
            return [
              "5 mg/ml oral solution (sugar free)", 
              // "Other"
            ];
          }
          break;
        default:
        return ["Other"];
      }
    } else if (medication === "Atomoxetine") {
      // If the brand is "Other", we can't provide specific dosage options
      if (medicineBrand === "Other") {
        return ["Other"];
      }
      
      // Otherwise, provide dosage options based on formulation and brand
      switch(formulation) {
        case "Modified-release":
          if (medicineBrand === "Atomoxetine") {
            return [
              "10 mg capsule",
              "18 mg capsule",
              "25 mg capsule",
              "40 mg capsule",
              "60 mg capsule",
              "80 mg capsule",
              "100 mg capsule",
              // "Other"
            ];
          }
          break;
        case "Liquid solution":
          if (medicineBrand === "Strattera") {
            return [
              "4 mg/ml oral solution",
              // "Other"
            ];
          }
          break;
        default:
          return ["Other"];
      }
    } else if (medication === "Methylphenidate Hydrochloride") {
      switch(formulation) {
        case "Modified-release":
          switch(medicineBrand) {
            case "Concerta XL (10 to 12 hour duration)":
            case "Xaggitin XL (12 hour duration)":
            case "Delmosart PR (12 hour duration)":
              return [
                "18 mg tablet",
                "27 mg tablet",
                "36 mg tablet",
                "54 mg tablet",
                // "Other"
              ];
            case "Medikinet XL (8 hour duration)":
              return [
                "5 mg capsule",
                "10 mg capsule",
                "20 mg capsule",
                "30 mg capsule",
                "40 mg capsule",
                "50 mg capsule",
                "60 mg capsule",
                // "Other"
              ];
            case "Equasym XL (8 hour duration)":
              return [
                "10 mg capsule",
                "20 mg capsule",
                "30 mg capsule",
                // "Other"
              ];
            case "Methylphenidate":
              return [
                "18 mg tablet",
                "27 mg tablet",
                "36 mg tablet",
                "54 mg tablet",
                // "Other"
              ];
            case "Xenidate XL":
              return [
                "18 mg tablet",
                "27 mg tablet",
                "36 mg tablet",
                "54 mg tablet",
                // "Other"
              ];
            case "Ritalin SR":
              return [
                "20 mg tablet",
                // "Other"
              ];
            case "Affenid XL":
              return [
                "18 mg tablet",
                "27 mg tablet",
                "36 mg tablet",
                "54 mg tablet",
                // "Other"
              ];
            case "Matoride XL":
              return [
                "18 mg tablet",
                "36 mg tablet",
                "54 mg tablet",
                // "Other"
              ];
            case "Meflynate XL":
            case "Metyrol XL":
              return [
                "10 mg capsule",
                "20 mg capsule",
                "30 mg capsule",
                "40 mg capsule",
                "60 mg capsule",
                // "Other"
              ];
            case "Ritalin XL":
              return [
                "10 mg capsule",
                "20 mg capsule",
                "30 mg capsule",
                "40 mg capsule",
                // "Other"
              ];
            default:
              return ["Other"];
          }
          
        case "Immediate-release (IR)":
          switch(medicineBrand) {
            case "Methylphenidate (4 hour duration)":
              return [
                "5 mg",
                "10 mg",
                "20 mg",
                // "Other"
              ];
            case "Ritalin (4 hour duration)":
              return [
                "10 mg",
                // "Other"
              ];
            case "Medikinet (<12 hour duration)":
              return [
                "5 mg tablet",
                "10 mg tablet",
                "20 mg tablet",
                // "Other"
              ];
            case "Tranquilyn":
              return [
                "5 mg tablet",
                "10 mg tablet",
                "20 mg tablet",
                // "Other"
              ];
            default:
              return ["Other"];
          }
          
        case "Liquid solution":
          if (medicineBrand === "Methylphenidate") {
            return [
              "2 mg/ml oral solution (sugar free)",
              "Other"
            ];
          }
          break;
          default:
            return ["Other"];
        }
      }
      
      // Default case
      return ["Other"];
    };
  
  // Define contraindications for each medication
  const contraindicationsByMedication = {
    "Methylphenidate Hydrochloride": [
      "Anorexia nervosa", "Arrhythmias", "Cardiomyopathy", "Cardiovascular disease", 
      "Cerebrovascular disorders", "Heart failure", "Hyperthyroidism", "Mania",
      "Phaeochromocytoma", "Psychosis", "Severe depression", "Severe hypertension", 
      "Structural cardiac abnormalities", "Suicidal tendencies", "Uncontrolled bipolar disorder", 
      "Vasculitis"
    ],
    "Lisdexamfetamine Mesilate": [
      "Advanced arteriosclerosis", "Agitated states", "Hyperthyroidism",
      "Moderate hypertension", "Severe hypertension", "Symptomatic cardiovascular disease"
    ],
    "Atomoxetine": [
      "Phaeochromocytoma", "Severe cardiovascular disease", "Severe cerebrovascular disease"
    ],
    "Dexamfetamine Sulfate": [
      "Advanced arteriosclerosis", "Anorexia", "Arrhythmias (life-threatening)",
      "Cardiomyopathies", "Cardiovascular disease", "Cerebrovascular disorders",
      "Heart failure", "History of alcohol abuse", "History of drug abuse",
      "Hyperexcitability", "Hyperthyroidism", "Moderate hypertension",
      "Psychiatric disorders", "Psychosis", "Severe hypertension",
      "Structural cardiac abnormalities", "Suicidal tendencies"
    ],
    "Guanfacine": [],
    "Other": []
  };

// Function to open the medication dialog and initialize with existing data
const openMedicationDialog = () => {
  if (medication) {
    // We're editing an existing medication - pre-select values
    
    // Extract the base medication from the full name
    const baseOptions = medicationOptions.filter(option => 
      medication.medicationName.includes(option)
    );
    
    const baseMedication = baseOptions.length > 0 ? baseOptions[0] : null;
    setSelectedMedication(baseMedication);
    
    // Set formulation
    setSelectedFormulation(medication.formulation);
    
    // Set medicine brand and handle "Other" case
    setSelectedMedicineBrand(medication.medicineBrand);
    if (medication.medicineBrand === "Other") {
      setOtherMedicineBrandText(medication.otherMedicineBrand || "");
    }
    
    // Set dosage and handle "Other" case
    setSelectedDosage(medication.dosage);
    if (medication.dosage === "Other") {
      setOtherDosageText(medication.otherDosage || "");
    }
    
    // Set titration schedule
    if (medication.titrationSchedule && Array.isArray(medication.titrationSchedule)) {
      setTitrationSchedule(medication.titrationSchedule);
    }
  } else {
    // Adding a new medication - reset all values
    setSelectedMedication(null);
    setSelectedFormulation(null);
    setSelectedMedicineBrand(null);
    setSelectedDosage(null);
    setOtherMedicineBrandText("");
    setOtherDosageText("");
    setTitrationSchedule([
      { week: 1, instructions: "" },
      { week: 2, instructions: "" },
      { week: 3, instructions: "" },
      { week: 4, instructions: "" }
    ]);
  }
  
  // Always start at step 1
  setDialogStep(1);
  setShowContraindications(false);
  setIsAddMedicationDialogOpen(true);
};

  // Fetching form structure from Firestore
  // We use useMemo to extract and memoize the sections of the form from the patient data model.
  // This ensures that we have the latest structure of the form, which is needed for rendering the correct questions and sections.
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);

  // Whether this org's PAD includes the Agreed Medication Plan (AMP) stage.
  const { isAgreedMedicationPlanEnabled, isPadTreatmentFirst = false, isAppointmentDatesEnabled = false } = useWorkflowConfig(patientDataModel, claims);

  // Ordered stage list for the clinician PAD — controls sidebar order and
  // display numbers. Treatment-first (SLaM) swaps sections 2/3.
  const padStageOrder = useMemo(
    () => getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }),
    [isPadTreatmentFirst, isAgreedMedicationPlanEnabled]
  );

  // Route guard: when AMP is disabled for this org (e.g. Well Life Clinic) the
  // stage is not part of the PAD, so redirect back to Health Checks. This
  // covers direct navigation / bookmarks that bypass the (now hidden) sidebar
  // entry, and — because the user never edits here — keeps hasChanges false so
  // the unmount-regeneration safety net never fires an empty medication-plan
  // generation. Waits for the patient model to load so we don't redirect on a
  // transient null before the workflow config resolves.
  useEffect(() => {
    if (patientDataModel && !isAgreedMedicationPlanEnabled) {
      navigate(`/patient/${patientId}${PREV_SECTION_LINK}`, { replace: true });
    }
  }, [patientDataModel, isAgreedMedicationPlanEnabled, navigate, patientId]);

  // Determines whether this user is the clinician assigned to the PAD stage.
  // When false the entire page renders in read-only mode.
  const canEdit = useCanEditStage(patientDataModel, 'PAD');

  // Registers dirty state with ProformaNavigationGuardContext and provides
  // unmount-based regeneration as a safety net for unmanaged exits (breadcrumb,
  // top nav, browser back). markRegenHandled() is called in handleSectionCompletion
  // to prevent double-firing when the managed Finish/Back/Sidebar path is used.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: CURRENT_STAGE_TITLE,
    sectionTitle: CURRENT_SECTION_TITLE,
    hasChanges,
    canEdit,
    patientDataModel,
  });

  const SECTIONS_PAD = useMemo(() => {
    const isDiagnosisADHD = isPadAdhdOutcomePositive(patientDataModel);
    const isCarryOutHealthChecksNow = readIsCarryOutHealthChecksNow(patientDataModel);
    const isBeginningMedicationNow = isCarryOutHealthChecksNow && patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['4']?.subsections?.['4']?.questions?.['2']?.answer?.value?.includes("Yes");

    return getPadSidebarSections(
      patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections,
      padStageOrder,
      buildPadSidebarUnlockFlags({
        patientDataModel,
        isPadTreatmentFirst,
        isDiagnosisADHD,
        isCarryOutHealthChecksNow,
        isBeginningMedicationNow,
      }),
    );
  }, [patientDataModel, padStageOrder, isPadTreatmentFirst]);
  
  // Treatment preferences always live on Treatment Options (Firestore key '3'),
  // even when the clinician-facing PAD order puts Treatment before Strategies.
  // Never derive this from sidebar display position ("2 sections ago") — that
  // breaks under treatment-first stageOrder and would mis-gate Add Medication.
  const patientsTreatmentPreferences = useMemo(
    () => readPatientTreatmentPreferences(patientDataModel),
    [patientDataModel]
  );

  const isPharmacologicalTreatment = useMemo(() => {
    // Check if patientsTreatmentPreferences (array) includes a pharmacological option
    return Array.isArray(patientsTreatmentPreferences) && 
    (patientsTreatmentPreferences.includes("Only pharmacological treatment") || 
    patientsTreatmentPreferences.includes("Both pharmacological and non-pharmacological treatment"));
  }, [patientsTreatmentPreferences]);
  
  const medicationPlanDetails = useMemo(() => {
    const section = SECTIONS_PAD.find(sec => sec.sectionNumber === CURRENT_SECTION_NUMBER);
    return section.subsections['1'];
  }, [SECTIONS_PAD]);

  const informedConsent = useMemo(() => {
    const section = SECTIONS_PAD.find(sec => sec.sectionNumber === CURRENT_SECTION_NUMBER);
    return section.subsections['2'];
  }, [SECTIONS_PAD]);

  // Setting up react-hook-form and initialising form values
  // This setup allows us to track changes, validate inputs, and manage form submissions.
  // We initialize form values from Firestore to ensure that the user's data is persisted across sessions and page navigations.
  // The useEffect hook is used to set initial values only once, preventing overwrites during user interaction.
  const { setValue, watch, formState: { isDirty } } = useForm();
  const formState = watch();

//   const initialValuesRef = useRef({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Update initializeFormValues function in useEffect
useEffect(() => {
  const initializeFormValues = (questions) => {
    const sectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[CURRENT_SECTION_NUMBER];
    if (sectionData) {
      // Initialize medication if it exists
      if (sectionData.medication) {
        setMedication(sectionData.medication);
        
        // Initialize titration schedule if it exists
        if (sectionData.medication.titrationSchedule && Array.isArray(sectionData.medication.titrationSchedule)) {
          setTitrationSchedule(sectionData.medication.titrationSchedule);
        }
      }
      
      // Initialize consent checkboxes
      Object.entries(informedConsent.questions).forEach(([key, question]) => {
        const fieldName = question.firestoreFieldName;
        if (sectionData[fieldName] !== undefined) {
          setValue(fieldName, sectionData[fieldName]);
        }
      });
    }
  };

  if (!isInitialized) {
    initializeFormValues(medicationPlanDetails.questions);
    setIsInitialized(true);
  }
}, [patientDataModel, setValue, medicationPlanDetails, informedConsent, isInitialized]);

  // Function to add a single medication
const addMedication = (medicationData) => {
  if (!canEdit) return;
  setMedication(medicationData);
  
  // Update Firestore
  updatePatientFirestore(patientId, {
    '5_postAssessmentDiscussion': {
      sections: {
        [CURRENT_SECTION_NUMBER]: {
          medication: medicationData
        }
      }
    }
  });
  
  setHasChanges(true);
  setIsAddMedicationDialogOpen(false);
};

// Handle adding a week to titration schedule - now with a limit of 4 weeks
const addWeekToSchedule = () => {
  // Don't add if we already have 4 weeks
  if (titrationSchedule.length >= 4) {
    return;
  }
  
  const nextWeekNumber = titrationSchedule.length > 0 
    ? Math.max(...titrationSchedule.map(item => item.week)) + 1 
    : 1;
    
  setTitrationSchedule([
    ...titrationSchedule, 
    { week: nextWeekNumber, instructions: "" }
  ]);
};

// Handle removing a week from titration schedule
const removeWeekFromSchedule = (weekToRemove) => {
  setTitrationSchedule(titrationSchedule.filter(item => item.week !== weekToRemove));
};

// Handle titration schedule input change
const handleTitrationChange = (week, instructions) => {
  setTitrationSchedule(
    titrationSchedule.map(item => 
      item.week === week ? { ...item, instructions } : item
    )
  );
};

  // Handle medication selection
  const handleMedicationChange = (medication) => {
    setSelectedMedication(medication);
    
    // Reset subsequent selections when medication changes
    setSelectedFormulation(null);
    setSelectedMedicineBrand(null);
    setSelectedDosage(null);
    
    // For Guanfacine, we know there's only one formulation option
    if (medication === "Guanfacine") {
      setSelectedFormulation("N/A");
    }
    
    setShowContraindications(false);
  };
  
  // Handle form submission to collect all data
  const handleMedicationSubmit = () => {
    // Construct the medication name from the selections
    let medicationName = selectedMedication;
    
    if (selectedMedication === "Other") {
      medicationName = document.getElementById("otherMedicationInput").value || "Other medication";
    }
        
    const medicationData = {
      medicationName: medicationName,
      stimulantOrNonStimulant: selectedMedication === "Methylphenidate Hydrochloride" || 
                               selectedMedication === "Lisdexamfetamine Mesilate" ||
                               selectedMedication === "Dexamfetamine Sulfate" 
                               ? "Stimulant" : "Non-stimulant",
      formulation: selectedFormulation,
      medicineBrand: selectedMedicineBrand,
      otherMedicineBrand: otherMedicineBrandText,
      dosage: selectedDosage,
      otherDosage: otherDosageText,  
      titrationSchedule: titrationSchedule.filter(item => item.instructions.trim() !== ""),       
    };
    
    addMedication(medicationData);
  
  // Reset state after submission
  setIsAddMedicationDialogOpen(false);
  setDialogStep(1);
};
  
  // Check if the next button should be enabled for the current step
  const isNextButtonEnabled = () => {
    switch (dialogStep) {
      case 1: // Medication selection
        return !!selectedMedication;
      case 2: // Formulation selection
        return !!selectedFormulation;
      case 3: // Medicine brand selection
        if (selectedMedicineBrand === "Other") {
          return otherMedicineBrandText.trim() !== "";
        }
        return !!selectedMedicineBrand;
      case 4: // Dosage selection
        if (selectedDosage === "Other") {
          return otherDosageText.trim() !== "";
        }
        return !!selectedDosage;
      case 5: // Titration schedule
        // Check that all remaining weeks have non-empty instructions
        if (titrationSchedule.length === 0) {
          // If no weeks are defined, we can proceed 
          return true;
        }
        
        // Check each week has non-empty instructions
        const allWeeksHaveInstructions = titrationSchedule.every(
          item => item.instructions && item.instructions.trim() !== ""
        );
        
        return allWeeksHaveInstructions;
      default:
        return false;
    }
  };
  
  // Updating Firestore with form values
  // We use a callback to update Firestore with the current form values, so any changes made by the user are persisted in the database
  // The isDirty flag from react-hook-form is used to check if any form field has been modified, preventing unnecessary writes to Firestore.
  // This approach ensures data persistence without interfering with the user's immediate input experience.
  // Modify updateFirestore to include medications
  const updateFirestore = useCallback((newUpdates) => {
    if (isDirty || newUpdates.medication) {
      updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [CURRENT_SECTION_NUMBER]: newUpdates
          }
        }
      });
    }
  }, [patientId, updatePatientFirestore, isDirty]);  

  const handleCheckboxChange = (fieldName, option) => {
    if (!canEdit) return;
    const currentSelections = new Set(formState[fieldName] || []);
    if (currentSelections.has(option)) {
      currentSelections.delete(option);
    } else {
      currentSelections.add(option);
    }
    setValue(fieldName, Array.from(currentSelections));
    updateFirestore({ [fieldName]: Array.from(currentSelections) });
    setHasChanges(true);
  };

  // Handle saving any changes before moving to a new section so user can move
  // through the form without losing progress. Conditional guards prevent redundant
  // Firestore writes and LLM generation calls on revisits where nothing changed.
  const handleSectionCompletion = useCallback(() => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[CURRENT_SECTION_NUMBER];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(CURRENT_SECTION_TITLE) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    if (needsStatusWrite) {
      updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [CURRENT_SECTION_NUMBER]: { status: 'completed', progress: 100 }
          }
        }
      });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, CURRENT_STAGE_TITLE, CURRENT_SECTION_TITLE, [], dataReady);
    }

    return dataReady;
  }, [canEdit, updatePatientFirestore, patientId, patientDataModel, hasChanges, flushPendingUpdates, markRegenHandled]);

  const [isNavigating, setIsNavigating] = useState(false);

  const handleNavigation = useCallback((link) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${link}`);
    });
  }, [isNavigating, patientId, navigate, handleSectionCompletion]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, handleSectionCompletion]);

  const handleGenerateReport = () => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  };
  
  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };
  
  const handleNextPageButtonClick = () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/documents`);
  };
  
  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await handleSectionCompletion();
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error completing section:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Add state for the preset dialog
// const [isPresetDialogOpen, setIsPresetDialogOpen] = useState(false);

// Add state to check if user is from Phoenix organization
const [isPhoenixOrg, setIsPhoenixOrg] = useState(false);

// Check organisation from claims without forcing token refresh
useEffect(() => {
  if (!claims?.organisation) {
    setIsPhoenixOrg(false);
    return;
  }
  setIsPhoenixOrg(claims.organisation === "Phoenix Mental Health Services");
}, [claims?.organisation]);


//   // Function to apply a preset medication plan
// const applyPreset = (presetType) => {
//   let presetData;
  
//   if (presetType === "Methylphenidate Hydrochloride") {
//     presetData = {
//       medicationName: "Methylphenidate Hydrochloride",
//       stimulantOrNonStimulant: "Stimulant",
//       formulation: "Modified-release",
//       medicineBrand: "Ritalin SR",
//       dosage: "20 mg tablet",
//       titrationSchedule: [
//         { week: 1, instructions: "Take 1 capsule per day" },
//         { week: 2, instructions: "Take 2 capsules per day" },
//         { week: 3, instructions: "Take 1 capsule per day" },
//         { week: 4, instructions: "Stop the medication and have your follow-up appointment" }
//       ]
//     };
//   } else if (presetType === "Lisdexamfetamine Mesilate") {
//     presetData = {
//       medicationName: "Lisdexamfetamine Mesilate",
//       stimulantOrNonStimulant: "Stimulant",
//       formulation: "N/A",
//       medicineBrand: "Elvanse Adult®",
//       dosage: "30 mg capsule",
//       titrationSchedule: [
//         { week: 1, instructions: "Take 1 capsule per day" },
//         { week: 2, instructions: "Take 2 capsules per day" },
//         { week: 3, instructions: "Take 1 capsule per day" },
//         { week: 4, instructions: "Stop the medication and have your follow-up appointment" }
//       ]
//     };
//   }
  
//   if (presetData) {
//     // Add the medication using the preset data
//     addMedication(presetData);
//   }
  
//   // Close the preset dialog
//   setIsPresetDialogOpen(false);
// };

  const breadcrumbTuples = [
    
    ["Patient Overview", `/patient/${patientId}`],
    [CURRENT_STAGE_TITLE],
    [CURRENT_SECTION_TITLE]
  ];


  // Rendering the page
  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <PADSideNavbar
          sections={SECTIONS_PAD}
          currentSectionIndex={getPadSidebarIndex(padStageOrder, '5')}
          navigateToSection={navigateToSection}
          isNavigating={isSaving || isNavigating}
        />
        <div className="flex-grow p-8">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => handleNavigation(PREV_SECTION_LINK)}
              disabled={isSaving || isNavigating}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
              aria-label="Back to Previous Section"
            >
              {(isSaving || isNavigating) ? (
                <ButtonSpinner className="w-5 h-5 mr-2" />
              ) : (
                <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              )}
              <span className="font-bold">{(isSaving || isNavigating) ? 'Loading...' : `Back to ${PREV_SECTION_TITLE}`}</span>
            </button>
            {canEdit && (
              <button
                onClick={handleFinish}
                disabled={isSaving || isNavigating}
                className={`flex items-center py-2 px-4 rounded-md shadow-md transition duration-100 bg-violet-900 text-white ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed' : 'hover:bg-violet-800'}`}
              >
                <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                {isSaving ? (
                  <ButtonSpinner className="w-5 h-5 ml-2" />
                ) : (
                  <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                )}
              </button>
            )}
          </div>

          {/* Title */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{CURRENT_SECTION_NUMBER}. {CURRENT_SECTION_TITLE}</h2>
          </div>

          {/* Conditional Banner */}
          {!isPharmacologicalTreatment && (
            <div className="mb-4 p-3 text-sm font-medium text-left bg-violet-100 border border-violet-300 rounded-xl text-gray-900 flex items-start gap-2">
              <InformationCircleIcon className="w-5 h-5 text-violet-800 mt-0.5 flex-shrink-0" />
              <span>
              Given that pharmacological treatment options are not being considered, this stage can be skipped and will not appear in the Assessment Report. 
              Click 'Finish Post-Assessment Discussion' to proceed.
              </span>
            </div>
          )}


{/* Medication Plan Card (replacing table) */}
<div className="flex mb-4 text-left">
  <div className="w-full p-8 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-start gap-6">
    <div className="w-full flex justify-between items-center mb-4">
      <div className="text-left text-slate-700 text-xl font-semibold leading-loose">
        {medicationPlanDetails.subsectionTitleDisplay}
      </div>
      {!medication ? (
    canEdit ? (
      <div className="flex space-x-2">
        <button
          onClick={() => openMedicationDialog()}
          disabled={!isPharmacologicalTreatment}
          className={`flex items-center py-2 px-4 rounded-md ${
            isPharmacologicalTreatment
              ? "bg-violet-900 hover:bg-violet-800 text-white" 
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          } transition duration-100`}
        >
          <PlusCircleIcon className="w-5 h-5 mr-2" aria-hidden="true" />
          <span className="font-semibold">{isPhoenixOrg ? "Add Custom Medication" : "Add Medication"}</span>
        </button>
      </div>
    ) : <div></div>
      ) : (
        <div></div>
      )}
    </div>
    
    {!medication ? (
      <div className="w-full text-center py-8 text-gray-500">
        {isPharmacologicalTreatment 
          ? "No medication added to the plan yet."
          : "No pharmacological treatment has been selected for this patient."}
      </div>
    ) : (
      <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        {/* Card Header */}
        <div className="p-5 bg-violet-50 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-violet-900">{medication.medicationName}</h3>
          {canEdit && (
            <div className="flex space-x-4">
              <button
                onClick={() => openMedicationDialog()}
                className="text-violet-600 hover:text-violet-900 flex items-center font-semibold"
              >
                <PencilIcon className="w-4 h-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => {
                  setMedication(null);
                  updatePatientFirestore(patientId, {
                    '5_postAssessmentDiscussion': {
                      sections: {
                        [CURRENT_SECTION_NUMBER]: {
                          medication: null
                        }
                      }
                    }
                  });
                  setHasChanges(true);
                }}
                className="text-red-600 hover:text-red-900 flex items-center font-semibold"
              >
                <TrashIcon className="w-4 h-4 mr-1" />
                Remove
              </button>
            </div>
          )}
        </div>
        
        {/* Card Body - Medication Details */}
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
              <p className="text-sm text-gray-500 font-medium">Brand</p>
              <p className="text-gray-900 mt-1">{medication.medicineBrand}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Type</p>
              <p className="text-gray-900 mt-1">{medication.stimulantOrNonStimulant}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Formulation</p>
              <p className="text-gray-900 mt-1">{medication.formulation}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium">Dosage</p>
              <p className="text-gray-900 mt-1">{medication.dosage}</p>
            </div>
          </div>
          
          {/* Titration Schedule - Vertical Stacked Layout */}
{medication.titrationSchedule && medication.titrationSchedule.length > 0 && (
  <div className="mt-6">
    <h4 className="font-medium mb-3 text-gray-700 border-b pb-2">Titration Schedule</h4>
    <div className="space-y-2">
      {medication.titrationSchedule.map((item) => (
        <div key={item.week} className="flex border-b border-gray-100 pb-2">
          <div className="w-20 flex-shrink-0 font-medium text-gray-800">
            Week {item.week}:
          </div>
          <div className="flex-1 text-gray-700">
            {item.instructions}
          </div>
        </div>
      ))}
    </div>
  </div>
)}
        </div>
      </div>
    )}
  </div>
</div>

          {/* Question Panel 2: informedConsent */}
          <div className="flex mb-4">
            <div className="w-full p-8 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-start gap-6">
              <div className="w-full text-left text-slate-700 text-xl font-semibold leading-loose">{informedConsent.subsectionTitleDisplay}</div>
              {Object.entries(informedConsent.questions).map(([key, question]) => (
                <div key={key} className="mb-4 flex items-start">
                  <h3 className="text-left mt-2 mb-2 font-bold text-gray-700 w-96">{question.questionTitle}</h3>
                  <div className="w-96 ml-5">
                    {question.questionType === 'checkboxes' && question.checkboxOptions.map((option, index) => (
                      <div key={index} className="flex items-start mt-2">
                        <input
                          type="checkbox"
                          className="mr-2 mt-1"
                          checked={formState[question.firestoreFieldName]?.includes(option) || false}
                          onChange={() => handleCheckboxChange(question.firestoreFieldName, option)}
                          disabled={!isPharmacologicalTreatment || !canEdit}
                        />
                        <label className="text-sm text-left font-medium text-gray-700">{option}</label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end items-center mt-8 pt-4 border-t border-gray-200">
            {canEdit && (
              <button
                onClick={handleFinish}
                disabled={isSaving || isNavigating}
                className={`flex items-center py-2 px-4 rounded-md shadow-md transition duration-100 bg-violet-900 text-white ${(isSaving || isNavigating) ? 'opacity-60 cursor-not-allowed' : 'hover:bg-violet-800'}`}
              >
                <span className="font-bold">{isSaving ? "Saving Progress..." : "Finish Post-Assessment Discussion"}</span>
                {isSaving ? (
                  <ButtonSpinner className="w-5 h-5 ml-2" />
                ) : (
                  <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>              

      {/* Add Medication Dialog */}
      <AlertDialog.Root 
        open={isAddMedicationDialogOpen} 
        onOpenChange={(open) => {
          if (!open) {
            setIsAddMedicationDialogOpen(false);
            setSelectedMedication(null);
            setSelectedFormulation(null);
            setSelectedMedicineBrand(null);
            setSelectedDosage(null);
            setOtherMedicineBrandText("");
            setOtherDosageText("");
            setDialogStep(1);
            setShowContraindications(false);
          }
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <AlertDialog.Title className="text-xl font-bold">
                {medication ? 'Edit Medication' : 'Add Medication to Plan'}
              </AlertDialog.Title>
              <AlertDialog.Cancel asChild>
                <button className="absolute top-2 right-3 text-gray-500 hover:text-gray-700">
                  <span aria-hidden>×</span>
                </button>
              </AlertDialog.Cancel>
            </div>

            {/* Step 1: Choose Medication */}
            {dialogStep === 1 && (
              <div>
                <h3 className="font-medium mb-2">Choose Medication</h3>
                <div className="mb-4">
                  <ul className="space-y-2 mt-2 text-sm">
                    {medicationOptions.map((option) => {
                      const isSelected = selectedMedication === option;
                      const shouldShowOtherInput = isSelected && option === "Other";
                      
                      return (
                        <li
                          key={option}
                          onClick={() => handleMedicationChange(option)}
                          className={`
                            flex flex-col gap-3 p-2
                            rounded-lg transition-all duration-300 
                            cursor-pointer group                                  
                            ${isSelected
                              ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                              : 'bg-violet-100 hover:bg-violet-200'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`
                              flex-shrink-0 w-4 h-4 rounded-full border-2
                              transition-all duration-200 ease-in-out
                              ${isSelected
                                ? 'border-violet-700 bg-violet-700'
                                : 'border-gray-300 group-hover:border-violet-400'
                              }
                              flex items-center justify-center
                            `}>
                              {isSelected && (
                                <div className={`
                                  w-1.5 h-1.5 bg-white rounded-full
                                  transition-all duration-200 ease-in-out
                                `} />
                              )}
                            </div>
                            <span className={`font-semibold transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                              {option}
                            </span>
                          </div>

                          {shouldShowOtherInput && (
                            <textarea
                              placeholder="Please specify the medication name"
                              className="font-medium w-full mt-1 p-2 border rounded-md bg-white text-black min-h-[60px] resize-none overflow-hidden"
                              id="otherMedicationInput"
                              value={otherMedicineBrandText}
                              onChange={(e) => setOtherMedicineBrandText(e.target.value)}
                              onClick={(e) => e.stopPropagation()} // Prevent option deselection when clicking the input
                              onInput={(e) => {
                                // Auto-resize the textarea
                                e.target.style.height = 'auto';
                                e.target.style.height = (e.target.scrollHeight) + 'px';
                              }}
                              rows={2} // Start with 2 rows minimum
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {selectedMedication && contraindicationsByMedication[selectedMedication]?.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                    <button
                      onClick={() => setShowContraindications(!showContraindications)}
                      className="flex items-center justify-between w-full text-left font-medium"
                    >
                      <span>Contraindications for {selectedMedication}</span>
                      {showContraindications ? (
                        <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    
                    {showContraindications && (
                      <div className="mt-2 pl-4">
                        <ul className="list-disc space-y-1 text-xs text-gray-700">
                          {contraindicationsByMedication[selectedMedication].map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 flex justify-end space-x-3">
                  <AlertDialog.Cancel asChild>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                    >
                      Cancel
                    </button>
                  </AlertDialog.Cancel>
                  <button
                    type="button"
                    disabled={!isNextButtonEnabled()}
                    onClick={() => {
                      // Skip to medicine brand step for Guanfacine since formulation is fixed as N/A
                      if (selectedMedication === "Guanfacine") {
                        setSelectedFormulation("N/A");
                        setDialogStep(3);
                      } else {
                        setDialogStep(2);
                      }
                    }}
                    className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 ${
                      isNextButtonEnabled()
                        ? "bg-violet-900 hover:bg-violet-800" 
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Choose Formulation */}
            {dialogStep === 2 && (
              <div>
                <h3 className="font-medium mb-2">Choose Formulation</h3>
                <div className="mb-4">
                  <ul className="space-y-2 mt-2 text-sm">
                    {getFormulationOptions(selectedMedication).map((option) => {
                      const isSelected = selectedFormulation === option;
                      
                      return (
                        <li
                          key={option}
                          onClick={() => setSelectedFormulation(option)}
                          className={`
                            flex flex-col gap-3 p-2 
                            rounded-lg transition-all duration-300 
                            cursor-pointer group                                        
                            ${isSelected
                              ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                              : 'bg-violet-100 hover:bg-violet-200'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`
                              flex-shrink-0 w-4 h-4 rounded-full border-2
                              transition-all duration-200 ease-in-out
                              ${isSelected
                                ? 'border-violet-700 bg-violet-700'
                                : 'border-gray-300 group-hover:border-violet-400'
                              }
                              flex items-center justify-center
                            `}>
                              {isSelected && (
                                <div className={`
                                  w-1.5 h-1.5 bg-white rounded-full
                                  transition-all duration-200 ease-in-out
                                `} />
                              )}
                            </div>
                            <span className={`font-semibold transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                              {option}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setDialogStep(1)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    disabled={!isNextButtonEnabled()}
                    onClick={() => setDialogStep(3)}
                    className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 ${
                      isNextButtonEnabled()
                        ? "bg-violet-900 hover:bg-violet-800" 
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Choose Medicine Brand */}
            {dialogStep === 3 && (
              <div>
                <h3 className="font-medium mb-2">Choose Medicine Brand</h3>
                <div className="mb-4">
                  <ul className="space-y-2 mt-2 text-sm">
                    {getMedicineBrandOptions(selectedMedication, selectedFormulation).map((option) => {
                      const isSelected = selectedMedicineBrand === option;
                      const shouldShowOtherInput = isSelected && option === "Other";
                      
                      return (
                        <li
                          key={option}
                          onClick={() => setSelectedMedicineBrand(option)}
                          className={`
                            flex flex-col gap-3 p-2 
                            rounded-lg transition-all duration-300 
                            cursor-pointer group                                        
                            ${isSelected
                              ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                              : 'bg-violet-100 hover:bg-violet-200'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`
                              flex-shrink-0 w-4 h-4 rounded-full border-2
                              transition-all duration-200 ease-in-out
                              ${isSelected
                                ? 'border-violet-700 bg-violet-700'
                                : 'border-gray-300 group-hover:border-violet-400'
                              }
                              flex items-center justify-center
                            `}>
                              {isSelected && (
                                <div className={`
                                  w-1.5 h-1.5 bg-white rounded-full
                                  transition-all duration-200 ease-in-out
                                `} />
                              )}
                            </div>
                            <span className={`font-semibold transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                              {option}
                            </span>
                          </div>

                          {shouldShowOtherInput && (
                            <textarea
                              placeholder="Please specify the brand name"
                              className="font-medium w-full mt-1 p-2 border rounded-md bg-white text-black min-h-[60px] resize-none overflow-hidden"
                              value={otherMedicineBrandText}
                              onChange={(e) => setOtherMedicineBrandText(e.target.value)}
                              onClick={(e) => e.stopPropagation()} // Prevent option deselection when clicking the input
                              onInput={(e) => {
                                // Auto-resize the textarea
                                e.target.style.height = 'auto';
                                e.target.style.height = (e.target.scrollHeight) + 'px';
                              }}
                              rows={2} // Start with 2 rows minimum
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      // Go back to step 1 for Guanfacine (skipping formulation)
                      setDialogStep(selectedMedication === "Guanfacine" ? 1 : 2);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    disabled={!isNextButtonEnabled()}
                    onClick={() => setDialogStep(4)}
                    className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 ${
                      isNextButtonEnabled()
                        ? "bg-violet-900 hover:bg-violet-800" 
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Choose Dosage */}
            {dialogStep === 4 && (
              <div>
                <h3 className="font-medium mb-2">Choose Dosage</h3>
                <div className="mb-4">
                  <ul className="space-y-2 mt-2 text-sm">
                    {getDosageOptions(selectedMedication, selectedFormulation, selectedMedicineBrand).map((option) => {
                      const isSelected = selectedDosage === option;
                      const shouldShowOtherInput = isSelected && option === "Other";
                      
                      return (
                        <li
                          key={option}
                          onClick={() => setSelectedDosage(option)}
                          className={`
                            flex flex-col gap-3 p-2 
                            rounded-lg transition-all duration-300 
                            cursor-pointer group                                        
                            ${isSelected
                              ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                              : 'bg-violet-100 hover:bg-violet-200'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`
                              flex-shrink-0 w-4 h-4 rounded-full border-2
                              transition-all duration-200 ease-in-out
                              ${isSelected
                                ? 'border-violet-700 bg-violet-700'
                                : 'border-gray-300 group-hover:border-violet-400'
                              }
                              flex items-center justify-center
                            `}>
                              {isSelected && (
                                <div className={`
                                  w-1.5 h-1.5 bg-white rounded-full
                                  transition-all duration-200 ease-in-out
                                `} />
                              )}
                            </div>
                            <span className={`font-semibold transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                              {option}
                            </span>
                          </div>

                          {shouldShowOtherInput && (
                            <textarea
                              placeholder="Please specify the dosage"
                              className="font-medium w-full mt-1 p-2 border rounded-md bg-white text-black min-h-[60px] resize-none overflow-hidden"
                              value={otherDosageText}
                              onChange={(e) => setOtherDosageText(e.target.value)}
                              onClick={(e) => e.stopPropagation()} // Prevent option deselection when clicking the input
                              onInput={(e) => {
                                // Auto-resize the textarea
                                e.target.style.height = 'auto';
                                e.target.style.height = (e.target.scrollHeight) + 'px';
                              }}
                              rows={2} // Start with 2 rows minimum
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>                

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setDialogStep(3)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                  >
                    Back
                  </button>
                  <button
    type="button"
    disabled={!isNextButtonEnabled()}
    onClick={() => setDialogStep(5)}
    className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 ${
      isNextButtonEnabled()
        ? "bg-violet-900 hover:bg-violet-800" 
        : "bg-gray-300 cursor-not-allowed"
    }`}
  >
    Next
  </button>
                </div>
              </div>
            )}

           {/* Add Medication Dialog - Titration Schedule with 4-week limit */}
{dialogStep === 5 && (
  <div>
    <h3 className="font-medium mb-2">Titration Schedule</h3>
    <div className="mb-4">
      <p className="text-sm text-gray-600 mb-3">
        Please provide instructions for each week of the medication schedule (max. 4 weeks):
      </p>
      
      <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
        {titrationSchedule.map((item) => (
          <div key={item.week} className="flex items-start space-x-2">
            <div className="w-20 flex-shrink-0 pt-2 font-medium">
              Week {item.week}:
            </div>
            <div className="flex-grow">
              <input
                type="text"
                value={item.instructions}
                onChange={(e) => handleTitrationChange(item.week, e.target.value)}
                // Neutral placeholders — avoid encoding a specific tablet/
                // capsule count (the old week-2 "Take 2 capsules" example
                // reinforced the AMP weekly LLM failure mode).
                placeholder={
                  item.week === 1 ? "e.g. Take one in the morning" :
                  item.week === 2 ? "e.g. Continue as week 1" :
                  item.week === 3 ? "e.g. Continue as week 1" :
                  item.week === 4 ? "e.g. Stop and review" :
                  `Instructions for week ${item.week}`
                }
                className="w-full p-2 border rounded-md text-sm"
              />
            </div>
            <div className="w-8 flex-shrink-0">
        {titrationSchedule.length > 1 && item.week === Math.max(...titrationSchedule.map(w => w.week)) ? (
          <button
            type="button"
            onClick={() => removeWeekFromSchedule(item.week)}
            className="text-red-500 hover:text-red-700 p-2"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        ) : null}
      </div>
          </div>
        ))}
      </div>
      
      {titrationSchedule.length < 4 && (
        <button
          type="button"
          onClick={addWeekToSchedule}
          className="mt-3 flex items-center text-sm text-violet-700 hover:text-violet-900"
        >
          <PlusCircleIcon className="w-4 h-4 mr-1" />
          Add another week
        </button>
      )}
    </div>
    
    <div className="mt-6 flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setDialogStep(4)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
      >
        Back
      </button>
      <button
        type="button"
        onClick={handleMedicationSubmit}
        className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-violet-900 hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
      >
        Save Medication Plan
      </button>
    </div>
  </div>
)}
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
      

      {/* Add Preset Dialog */}
{/* <AlertDialog.Root
  open={isPresetDialogOpen}
  onOpenChange={setIsPresetDialogOpen}
>
  <AlertDialog.Portal>
    <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
    <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[500px] max-w-[90vw]">
      <AlertDialog.Title className="text-xl font-bold mb-4">
        Select Medication Preset
      </AlertDialog.Title>
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Select one of the following preset medication plans:
        </p>
        <div className="space-y-3">
          <div 
            onClick={() => applyPreset("Methylphenidate Hydrochloride")}
            className="p-4 border rounded-lg cursor-pointer hover:bg-violet-50 transition-colors"
          >
            <h3 className="font-medium text-violet-800">Methylphenidate Hydrochloride</h3>
            <p className="text-sm text-gray-600 mt-1">Ritalin SR, 20 mg tablet, Modified-release</p>
          </div>
          
          <div 
            onClick={() => applyPreset("Lisdexamfetamine Mesilate")}
            className="p-4 border rounded-lg cursor-pointer hover:bg-violet-50 transition-colors"
          >
            <h3 className="font-medium text-violet-800">Lisdexamfetamine Mesilate</h3>
            <p className="text-sm text-gray-600 mt-1">Elvanse Adult®, 30 mg capsule</p>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <AlertDialog.Cancel asChild>
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50">
            Cancel
          </button>
        </AlertDialog.Cancel>
      </div>
    </AlertDialog.Content>
  </AlertDialog.Portal>
</AlertDialog.Root> */}

      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Post-Assessment Discussion Complete"
        width={600}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton onClick={handleGenerateReport} />
          </>
        }
      >
        <SummarySection title="Summary:">
          The Post-Assessment Discussion has been completed. Would you like to generate the report?
        </SummarySection>
      </ModuleCompletionDialog>

<GenerateReportProgressDialog 
  reportStage="Post-Assessment Discussion"
  patientId={patientId}
  clinicianId={clinicianId}
  handleNextPageButtonClick={handleNextPageButtonClick}
  isGenerating={isGenerating}
  onCancel={handleCancelGeneration}
/>
    </Layout>
  );
}

